import { clsx } from '@alexis/helpers/clsx';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { convertDurationInMinutesToHoursAndMinutes } from '@helpers/utils';

import { getIsRtlState, getTranslationsState } from '@redux/selectors';

import type { SegmentViewModel } from '@wegoTypes/flights/segmentViewModel';

import styles from './SegmentView.module.scss';

interface SegmentViewProps {
  segmentViewModel: SegmentViewModel;
}

const SegmentView: React.FC<SegmentViewProps> = ({ segmentViewModel }) => {
  const translations = useSelector(getTranslationsState);
  const isRtl = useSelector(getIsRtlState);

  const stopOverTimeTooltipText = useMemo(() => {
    const stopOverTimeText = convertDurationInMinutesToHoursAndMinutes(
      segmentViewModel.segment.stopoverDurationMinutes,
    );

    if (isRtl)
      return (
        <>
          <span>{translations.stopover}</span>{' '}
          <span data-testid='stopover-duration'>{stopOverTimeText}</span>
        </>
      );

    return (
      <>
        <span data-testid='stopover-duration'>{stopOverTimeText}</span>{' '}
        <span>{(translations.stopover as string)?.toLowerCase()}</span>
      </>
    );
  }, [isRtl, segmentViewModel.segment.stopoverDurationMinutes, translations.stopover]);

  return (
    <div className={clsx(styles.container, isRtl && styles.rtl)}>
      <div
        data-pw='leg_stopoverInfo'
        data-testid='stopover'
        className={styles.tooltipContainer}
        style={{
          [isRtl ? 'right' : 'left']: `${segmentViewModel.pointStartPercentage}%`,
          width: `${segmentViewModel.pointWidthPercentage}%`,
        }}
      >
        <div className={styles.point} style={{ [isRtl ? 'right' : 'left']: `0` }}></div>

        <div
          className={styles.code}
          style={{ [isRtl ? 'right' : 'left']: `50%` }}
          data-testid='stopover-airport'
        >
          {segmentViewModel.segment.arrivalAirportCode}
        </div>

        <div className={clsx(styles.tooltip, styles.bottom)} role='tooltip'>
          {stopOverTimeTooltipText} <br /> {segmentViewModel.segment.arrivalAirport?.name}
        </div>
      </div>
    </div>
  );
};

export default SegmentView;
